import React from "react";
import { useNavigate } from "react-router-dom";
import { IdCard, CreditCard, Award, Sparkles, ShieldCheck, Bell, Lock, Settings, Server, ChevronRight, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAssistant } from "@/components/earnos/Assistant";
import { Pill } from "@/components/earnos/kit";
import { colors } from "@/lib/theme";

export default function You() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { open } = useAssistant();

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const items = [
    { icon: IdCard, tint: colors.brand, label: "Economic Identity", sub: "Goals, skills, resources & boundaries", onClick: () => navigate("/settings"), testID: "you-identity" },
    { icon: CreditCard, tint: colors.brandSecondary, label: "Membership & Billing", sub: "Plan, AI usage, fees & payments", onClick: () => navigate("/billing"), testID: "you-billing" },
    { icon: Award, tint: colors.success, label: "Reputation", sub: "Verified vs self-reported signals", onClick: () => navigate("/reputation"), testID: "you-reputation" },
    { icon: Sparkles, tint: colors.info, label: "Ask EARNOS", sub: "Your AI income companion", onClick: open, testID: "you-assistant" },
    { icon: ShieldCheck, tint: colors.brandTertiary, label: "Trust & Safety", sub: "What AI can do, fees, and your data", onClick: () => navigate("/trust-center"), testID: "you-trust" },
    { icon: Bell, tint: colors.warning, label: "Notifications", sub: "Alerts & quiet hours", onClick: () => navigate("/settings"), testID: "you-notifications" },
    { icon: Lock, tint: colors.onSurfaceSecondary, label: "Data & Privacy", sub: "Data export and account controls", onClick: () => navigate("/settings"), testID: "you-security" },
    { icon: Settings, tint: colors.onSurfaceSecondary, label: "Settings", sub: "Engine, demo mode & more", onClick: () => navigate("/settings"), testID: "you-settings" },
    { icon: Settings, tint: colors.onSurfaceSecondary, label: "Reality Check", sub: "Assess an idea against your resources and evidence", onClick: () => navigate("/reality-check"), testID: "you-reality-check" },
    { icon: Settings, tint: colors.onSurfaceSecondary, label: "Economic Graph", sub: "Trace saved records, missions and deliverables", onClick: () => navigate("/economic-graph"), testID: "you-economic-graph" },
    ...(isAdmin ? [{ icon: Server, tint: colors.error, label: user?.role === "super_admin" ? "Owner Console" : "Admin Console", sub: "Users, activity, AI cost & controls", onClick: () => navigate("/admin"), testID: "you-admin" }] : []),
  ];

  const doLogout = async () => {
    await logout();
    navigate("/auth", { replace: true });
  };

  return (
    <div className="animate-fade-in max-w-3xl">
      <h1 className="text-3xl font-extrabold tracking-tight text-ink mb-5">You</h1>

      <div className="flex items-center gap-4 rounded-2xl border border-line bg-surface-2 p-5">
        <div className="w-14 h-14 rounded-full bg-brand flex items-center justify-center text-white text-xl font-extrabold shrink-0">
          {(user?.first_name || "?")[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xl font-bold text-ink truncate">{user?.first_name}</p>
          <p className="text-sm text-ink-2 truncate">{user?.email}</p>
        </div>
        {user?.demo_mode ? <Pill label="Demo" color="#F59E0B" /> : <Pill label={(user?.plan || "start").toUpperCase()} />}
      </div>

      <div className="space-y-3 mt-5">
        {items.map((it) => (
          <button key={it.label} onClick={it.onClick} data-testid={it.testID} className="w-full flex items-center gap-3 rounded-xl border border-line bg-surface-2 p-4 hover:border-line-strong transition-colors text-left">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: it.tint + "22" }}>
              <it.icon size={20} style={{ color: it.tint }} />
            </div>
            <div className="flex-1 min-w-0"><p className="text-base font-bold text-ink">{it.label}</p><p className="text-sm text-ink-2">{it.sub}</p></div>
            <ChevronRight size={20} className="text-ink-2" />
          </button>
        ))}
      </div>

      <button onClick={doLogout} data-testid="you-logout" className="w-full flex items-center justify-center gap-2 mt-6 h-12 rounded-full bg-surface-3 border border-line text-danger font-bold hover:border-danger/50 transition-colors">
        <LogOut size={18} /> Sign out
      </button>
    </div>
  );
}
